import { formatMinorAmount } from '@/utils/currency';
import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Table,
  Tag,
  Space,
  Button,
  Select,
  Input,
  DatePicker,
  Card,
  Typography,
  Modal,
  Form,
  InputNumber,
  Drawer,
  Descriptions,
  App,
} from 'antd';
import { SearchOutlined, ExperimentOutlined, ReloadOutlined } from '@ant-design/icons';
import { ThreeDSChallengeStatus, ThreeDSChallengeSource, ThreeDSChallenge } from '@mygate/common';
import { threeDSService } from '@/services/threeDS';
import dayjs from 'dayjs';

const { Text } = Typography;
const { RangePicker } = DatePicker;

const STATUS_COLORS: Record<string, string> = {
  PENDING: 'orange',
  APPROVED: 'green',
  DECLINED: 'red',
  TIMED_OUT: 'default',
  ERROR: 'volcano',
};

const SOURCE_COLORS: Record<string, string> = {
  PAYCENTER: 'blue',
  INTERNAL: 'orange',
};

export function ThreeDSChallengesPage() {
  const { message } = App.useApp();

  // Table state
  const [challenges, setChallenges] = useState<ThreeDSChallenge[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, limit: 20 });
  const initializedRef = useRef(false);

  // Filters
  const [statusFilter, setStatusFilter] = useState<ThreeDSChallengeStatus | undefined>();
  const [sourceFilter, setSourceFilter] = useState<ThreeDSChallengeSource | undefined>();
  const [userIdFilter, setUserIdFilter] = useState('');
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);

  // Detail drawer
  const [detailChallenge, setDetailChallenge] = useState<ThreeDSChallenge | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Test trigger modal
  const [testModalOpen, setTestModalOpen] = useState(false);
  const [testForm] = Form.useForm();
  const [triggerLoading, setTriggerLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await threeDSService.getChallengeLog({
        status: statusFilter,
        source: sourceFilter,
        userId: userIdFilter || undefined,
        startDate: dateRange?.[0]?.toISOString(),
        endDate: dateRange?.[1]?.toISOString(),
        page: pagination.page,
        limit: pagination.limit,
      });
      if (response.data) {
        setChallenges(response.data.challenges);
        setTotal(response.data.total);
      }
      if (!initializedRef.current) {
        initializedRef.current = true;
      }
    } catch (err) {
      message.error('Failed to load 3DS challenges');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, sourceFilter, userIdFilter, dateRange, pagination.page, pagination.limit, message]);

  // Initial load
  useEffect(() => {
    fetchData();
  }, []);

  // Refetch on pagination change (skip initial)
  useEffect(() => {
    if (initializedRef.current) {
      fetchData();
    }
  }, [pagination.page, pagination.limit]);

  const handleSearch = () => {
    if (pagination.page !== 1) {
      setPagination(prev => ({ ...prev, page: 1 }));
    } else {
      fetchData();
    }
  };

  const handleRowClick = async (record: ThreeDSChallenge) => {
    try {
      const response = await threeDSService.getChallengeDetail(record._id);
      if (response.data) {
        setDetailChallenge(response.data as ThreeDSChallenge);
        setDrawerOpen(true);
      }
    } catch {
      message.error('Failed to load challenge details');
    }
  };

  const handleTestTrigger = async () => {
    try {
      const values = await testForm.validateFields();
      setTriggerLoading(true);
      const response = await threeDSService.triggerTestChallenge({
        userId: values.userId,
        merchantName: values.merchantName,
        amount: values.amount,
        currencyCode: values.currencyCode || 'EUR',
        merchantCountry: values.merchantCountry || 'DE',
      });
      if (response.data) {
        message.success(`Test challenge created: ${response.data._id}`);
        setTestModalOpen(false);
        testForm.resetFields();
        fetchData();
      }
    } catch (err) {
      message.error('Failed to trigger test challenge');
    } finally {
      setTriggerLoading(false);
    }
  };

  const columns = [
    {
      title: 'Time',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (val: string, record: ThreeDSChallenge) => (
        <Button
          type="link"
          className="table-cell-link"
          onClick={event => {
            event.stopPropagation();
            void handleRowClick(record);
          }}
        >
          {dayjs(val).format('DD.MM.YYYY HH:mm:ss')}
        </Button>
      ),
    },
    {
      title: 'User',
      dataIndex: 'userId',
      key: 'userId',
      width: 180,
      render: (user: { firstName?: string; lastName?: string; phoneNumber?: string } | string) => {
        if (typeof user === 'object' && user) {
          return (
            <Space direction="vertical" size={0}>
              <Text strong>
                {user.firstName} {user.lastName}
              </Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {user.phoneNumber}
              </Text>
            </Space>
          );
        }
        return <Text type="secondary">{String(user)}</Text>;
      },
    },
    {
      title: 'Merchant',
      dataIndex: 'merchantName',
      key: 'merchantName',
      width: 150,
    },
    {
      title: 'Amount',
      key: 'amount',
      align: 'right' as const,
      width: 120,
      render: (_: unknown, record: ThreeDSChallenge) => {
        const formatted = formatMinorAmount(record.amount, record.currencyExponent);
        return `${formatted} ${record.currencyCode}`;
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (status: string) => <Tag color={STATUS_COLORS[status] || 'default'}>{status}</Tag>,
    },
    {
      title: 'Source',
      dataIndex: 'source',
      key: 'source',
      width: 100,
      render: (source: string) => <Tag color={SOURCE_COLORS[source] || 'default'}>{source}</Tag>,
    },
    {
      title: 'Response Time',
      key: 'responseTime',
      width: 120,
      render: (_: unknown, record: ThreeDSChallenge) => {
        if (!record.respondedAt) return '—';
        const created = new Date(record.createdAt).getTime();
        const responded = new Date(record.respondedAt).getTime();
        const diffSec = Math.round((responded - created) / 1000);
        if (diffSec < 60) return `${diffSec}s`;
        return `${Math.floor(diffSec / 60)}m ${diffSec % 60}s`;
      },
    },
  ];

  return (
    <>
      <div className="page-toolbar">
        <Typography.Text type="secondary">Authentifizierungsanfragen für Kartenzahlungen prüfen.</Typography.Text>
        <Button type="primary" icon={<ExperimentOutlined />} onClick={() => setTestModalOpen(true)}>
          Trigger Test
        </Button>
      </div>

      <div style={{ maxWidth: 1400 }}>
        {/* Filters */}
        <Card style={{ marginBottom: 16 }}>
          <Space wrap>
            <Select
              placeholder="Status"
              allowClear
              style={{ width: 140 }}
              value={statusFilter}
              onChange={setStatusFilter}
            >
              {Object.values(ThreeDSChallengeStatus).map(s => (
                <Select.Option key={s} value={s}>
                  {s}
                </Select.Option>
              ))}
            </Select>
            <Select
              placeholder="Source"
              allowClear
              style={{ width: 140 }}
              value={sourceFilter}
              onChange={setSourceFilter}
            >
              {Object.values(ThreeDSChallengeSource).map(s => (
                <Select.Option key={s} value={s}>
                  {s}
                </Select.Option>
              ))}
            </Select>
            <Input
              placeholder="User ID"
              style={{ width: 240 }}
              value={userIdFilter}
              onChange={e => setUserIdFilter(e.target.value)}
            />
            <RangePicker value={dateRange} onChange={val => setDateRange(val as [dayjs.Dayjs, dayjs.Dayjs] | null)} />
            <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
              Search
            </Button>
            <Button icon={<ReloadOutlined />} onClick={fetchData}>
              Refresh
            </Button>
          </Space>
        </Card>

        {/* Table */}
        <Card>
          <Table
            tableLayout="fixed"
            scroll={{ x: 940 }}
            dataSource={challenges}
            columns={columns}
            rowKey="_id"
            loading={loading}
            onRow={record => ({
              onClick: () => handleRowClick(record),
              style: { cursor: 'pointer' },
            })}
            pagination={{
              current: pagination.page,
              pageSize: pagination.limit,
              total,
              showSizeChanger: true,
              showTotal: t => `${t} total`,
              onChange: (page, pageSize) => setPagination({ page, limit: pageSize }),
            }}
          />
        </Card>
      </div>

      {/* Detail Drawer */}
      <Drawer title="Challenge Details" open={drawerOpen} onClose={() => setDrawerOpen(false)} width={500}>
        {detailChallenge && (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="ID">{detailChallenge._id}</Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={STATUS_COLORS[detailChallenge.status]}>{detailChallenge.status}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Source">
              <Tag color={SOURCE_COLORS[detailChallenge.source]}>{detailChallenge.source}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Merchant">{detailChallenge.merchantName}</Descriptions.Item>
            <Descriptions.Item label="Amount">
              {formatMinorAmount(detailChallenge.amount, detailChallenge.currencyExponent)}{' '}
              {detailChallenge.currencyCode}
            </Descriptions.Item>
            <Descriptions.Item label="Country">{detailChallenge.merchantCountry}</Descriptions.Item>
            <Descriptions.Item label="Auth Card ID">{detailChallenge.authcardid}</Descriptions.Item>
            <Descriptions.Item label="Ref">{detailChallenge.ref}</Descriptions.Item>
            <Descriptions.Item label="Protocol">{detailChallenge.protocolVersion}</Descriptions.Item>
            <Descriptions.Item label="Challenge Date">{detailChallenge.challengeDate}</Descriptions.Item>
            <Descriptions.Item label="Created">
              {dayjs(detailChallenge.createdAt).format('DD.MM.YYYY HH:mm:ss')}
            </Descriptions.Item>
            {detailChallenge.respondedAt && (
              <Descriptions.Item label="Responded">
                {dayjs(detailChallenge.respondedAt).format('DD.MM.YYYY HH:mm:ss')}
              </Descriptions.Item>
            )}
            {detailChallenge.timedOutAt && (
              <Descriptions.Item label="Timed Out">
                {dayjs(detailChallenge.timedOutAt).format('DD.MM.YYYY HH:mm:ss')}
              </Descriptions.Item>
            )}
            {detailChallenge.paycenterCallbackResult ? (
              <Descriptions.Item label="PayCenter Result">
                <pre style={{ fontSize: 11, whiteSpace: 'pre-wrap' }}>
                  {JSON.stringify(detailChallenge.paycenterCallbackResult, null, 2)}
                </pre>
              </Descriptions.Item>
            ) : null}
            {detailChallenge.paycenterCallbackError && (
              <Descriptions.Item label="PayCenter Error">
                <Text type="danger">{detailChallenge.paycenterCallbackError}</Text>
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Drawer>

      {/* Test Trigger Modal */}
      <Modal
        title="Trigger Test 3DS Challenge"
        open={testModalOpen}
        onCancel={() => setTestModalOpen(false)}
        onOk={handleTestTrigger}
        confirmLoading={triggerLoading}
        okText="Trigger"
      >
        <Form form={testForm} layout="vertical">
          <Form.Item name="userId" label="User ID" rules={[{ required: true, message: 'Required' }]}>
            <Input placeholder="MongoDB User ID" />
          </Form.Item>
          <Form.Item name="merchantName" label="Merchant Name" rules={[{ required: true, message: 'Required' }]}>
            <Input placeholder="e.g. Amazon" />
          </Form.Item>
          <Form.Item name="amount" label="Amount (cents)" rules={[{ required: true, message: 'Required' }]}>
            <InputNumber style={{ width: '100%' }} placeholder="e.g. 2499 for 24.99" min={1} />
          </Form.Item>
          <Form.Item name="currencyCode" label="Currency Code" initialValue="EUR">
            <Input />
          </Form.Item>
          <Form.Item name="merchantCountry" label="Merchant Country" initialValue="DE">
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
